import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { Reveal, MaskedLines, Overline, Magnetic } from "@/components/Motion";
import { usePage } from "@/settings";

const DEFAULT_SERVICES = [
  ...["Architecture Consultation", "Residential Design", "Architectural Design", "Interior Design",
      "Sustainable Architecture Consultation", "Material Consultation", "Renovation",
      "Concept Design", "Architectural Visualisation"].map((name) => ({ name, group: "Architecture", description: "", hidden: false })),
  ...["Portrait Commissions", "Custom Paintings", "Wall Murals", "Illustrations",
      "Art Installations", "Custom Creative Collaborations"].map((name) => ({ name, group: "Art", description: "", hidden: false })),
];

export const ENQUIRY_TYPES = ["Architecture", "Interior", "Consultation", "Artwork Commission", "Wall Art", "Workshop", "Collaboration", "Other"];

export function EnquiryForm({ testidPrefix = "enquiry" }) {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    type: params.get("type") || "Architecture",
    message: params.get("subject") ? `Regarding: ${params.get("subject")}\n` : "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — the studio will respond shortly.");
      setForm({ name: "", email: "", phone: "", type: form.type, message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-ink/25 focus:border-brand outline-none py-3 font-sans text-base placeholder:text-ink/30 transition-colors";

  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <input required data-testid={`${testidPrefix}-name`} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
        <input required type="email" data-testid={`${testidPrefix}-email`} value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
        <input data-testid={`${testidPrefix}-phone`} value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={inputCls} />
        <select data-testid={`${testidPrefix}-type`} value={form.type} onChange={set("type")} className={`${inputCls} cursor-pointer`}>
          {ENQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <textarea required rows={5} data-testid={`${testidPrefix}-message`} value={form.message} onChange={set("message")}
        placeholder="Tell us about your project, site, artwork or idea…" className={`${inputCls} resize-none`} />
      <Magnetic>
        <button type="submit" disabled={sending} data-testid={`${testidPrefix}-submit-btn`}
          className="bg-ink text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors disabled:opacity-50">
          {sending ? "Sending…" : "Send Enquiry"}
        </button>
      </Magnetic>
    </form>
  );
}

function ServiceGroup({ title, items, testid }) {
  if (!items.length) return null;
  return (
    <Reveal>
      <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-6">{title}</h2>
      <div data-testid={testid}>
        {items.map((s, i) => (
          <a key={s.name} href="#enquiry" data-testid={`${testid}-item-${i}`}
            className="group flex items-center justify-between py-4 border-b border-ink/15 hover:border-brand transition-colors">
            <span className="flex items-start gap-4 min-w-0">
              <span className="font-mono text-[10px] text-ink/40 pt-1.5">{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0">
                <span className="font-display text-xl md:text-2xl group-hover:text-brand transition-colors block leading-snug">{s.name}</span>
                {s.description && <span className="text-sm text-ink/50 block mt-1">{s.description}</span>}
              </span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 group-hover:text-brand transition-colors shrink-0 pl-4">Enquire</span>
          </a>
        ))}
      </div>
    </Reveal>
  );
}

export default function Services() {
  const pg = usePage("services");
  const services = (pg.services && pg.services.length ? pg.services : DEFAULT_SERVICES).filter((sv) => !sv.hidden);
  const archServices = services.filter((sv) => sv.group === "Architecture");
  const artServices = services.filter((sv) => sv.group === "Art");
  return (
    <main data-testid="services-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Overline>Services</Overline>
      <MaskedLines
        className="mt-6 mb-16"
        lineClassName="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight"
        lines={[pg.heading_1 || "What we can", pg.heading_2 || "make together."]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
        <ServiceGroup title="Architecture" items={archServices} testid="arch-services" />
        <ServiceGroup title="Art" items={artServices} testid="art-services" />
      </div>
      <Reveal className="bg-linen border border-ink/10 p-8 md:p-14" >
        <div id="enquiry" className="scroll-mt-32">
          <Overline>Start a Project</Overline>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 mb-10">{pg.form_heading || "Tell us what you're imagining."}</h2>
          <EnquiryForm testidPrefix="services-enquiry" />
        </div>
      </Reveal>
    </main>
  );
}
